/* Documents — HR / Company letters module.
   Admin-only (unless 'documents' feature flag enabled for all).

   Tabs:
     - Generated Documents (main)
     - Letter Templates
     - Approval Queue
*/
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Plus, Trash2, Download, Lock, Unlock, Send, Check, Eye, Edit3 } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { v: "offer_letter", l: "Offer Letter" },
  { v: "joining_letter", l: "Joining Letter" },
  { v: "appointment_letter", l: "Appointment Letter" },
  { v: "experience_letter", l: "Experience Letter" },
  { v: "relieving_letter", l: "Relieving Letter" },
  { v: "leave_letter", l: "Leave Letter" },
  { v: "salary_certificate", l: "Salary Certificate" },
  { v: "internship_letter", l: "Internship Letter" },
  { v: "vendor_letter", l: "Vendor Letter" },
  { v: "agreement", l: "Agreement" },
  { v: "nda", l: "NDA" },
  { v: "letterhead", l: "Company Letterhead" },
  { v: "custom", l: "Custom" },
];

const STATUS_COLORS = {
  draft: "bg-slate-500", reviewed: "bg-sky-500", approved: "bg-emerald-500", sent: "bg-violet-500",
};

const PLACEHOLDER_HINTS = [
  "{candidate_name}", "{job_title}", "{department}", "{salary}",
  "{joining_date}", "{address}", "{client_name}", "{hr_name}", "{authorized_signatory}",
];

export default function Documents() {
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);

  const refresh = async () => {
    try {
      const [tpl, all] = await Promise.all([api.get("/documents/templates"), api.get("/documents")]);
      setTemplates(tpl.data || []);
      setDocs(all.data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Documents module is disabled for your role");
      else toast.error("Failed to load documents");
    }
  };
  useEffect(() => { refresh(); }, []);

  const approvalQueue = useMemo(() => docs.filter((d) => d.status === "draft" || d.status === "reviewed"), [docs]);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><FileText className="size-3.5" /> HR Documents</div>
        <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Letters & official documents</h1>
        <p className="text-muted-foreground mt-1 text-sm">Create offer letters, NDAs, agreements and other HR documents with templates, placeholders, and approval workflow.</p>
      </div>

      <Tabs defaultValue="docs">
        <TabsList className="grid grid-cols-3 w-full max-w-[560px]" data-testid="docs-tabs">
          <TabsTrigger value="docs" data-testid="docs-tab-generated">Generated ({docs.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="docs-tab-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="queue" data-testid="docs-tab-queue">Approval queue ({approvalQueue.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-5">
          <DocList docs={docs} templates={templates} onRefresh={refresh} />
        </TabsContent>

        <TabsContent value="templates" className="mt-5">
          <TemplateList templates={templates} onRefresh={refresh} />
        </TabsContent>

        <TabsContent value="queue" className="mt-5">
          <ApprovalQueue docs={approvalQueue} onRefresh={refresh} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DocList({ docs, templates, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="flex justify-end mb-3"><Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="docs-new-document"><Plus className="size-4" /> New document</Button></div>
      {docs.length === 0 ? (
        <Card className="border-dashed border-border"><CardContent className="p-10 text-center text-sm text-muted-foreground">No documents generated yet.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="docs-list">
          {docs.map((d) => (
            <Card key={d.id} className="border-border hover:shadow-sm" data-testid={`doc-${d.id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`size-1.5 rounded-full ${STATUS_COLORS[d.status] || "bg-slate-500"}`} />
                    <span className="font-bold truncate">{d.title}</span>
                    <span className="chip bg-secondary text-foreground text-[10px]">{DOC_TYPES.find((x) => x.v === d.type)?.l || d.type}</span>
                    <span className="chip bg-secondary text-foreground text-[10px] uppercase">{d.status}</span>
                  </div>
                  {d.candidate_name && <div className="text-xs text-muted-foreground mt-1">Candidate: {d.candidate_name}</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(d)} className="gap-1" data-testid={`doc-edit-${d.id}`}><Edit3 className="size-3.5" /> Edit</Button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/export/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm px-3 h-9 rounded-md border border-border hover:bg-secondary lift" data-testid={`doc-pdf-${d.id}`}><Download className="size-3.5" /> PDF</a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/export/docx`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm px-3 h-9 rounded-md border border-border hover:bg-secondary lift" data-testid={`doc-dl-${d.id}`}><Download className="size-3.5" /> DOCX</a>
                <Button size="icon" variant="ghost" className="text-destructive" onClick={async () => { if (window.confirm("Delete this document?")) { await api.delete(`/documents/${d.id}`); toast.success("Deleted"); onRefresh(); } }} data-testid={`doc-del-${d.id}`}><Trash2 className="size-4" /></Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {(editing || creating) && <DocEditor doc={editing || {}} templates={templates} isNew={!editing} onClose={() => { setEditing(null); setCreating(false); onRefresh(); }} />}
    </>
  );
}

function DocEditor({ doc, templates, isNew, onClose }) {
  const [p, setP] = useState({
    title: doc.title || "Untitled document",
    type: doc.type || "custom",
    template_id: doc.template_id || "",
    candidate_name: doc.candidate_name || "",
    candidate_resume_id: doc.candidate_resume_id || "",
    body_html: doc.body_html || "",
    status: doc.status || "draft",
    placeholders: doc.placeholders || {},
  });
  const [saving, setSaving] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [mode, setMode] = useState("edit"); // 'edit' | 'letterhead'
  const [letterPreview, setLetterPreview] = useState(null);

  useEffect(() => {
    api.get("/resumes").then((r) => setResumes(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (t) setP((x) => ({ ...x, template_id: tid, type: t.type, body_html: t.body_html }));
  };

  const seedFromResume = async (rid) => {
    if (!rid || rid === "__none__") { setP((x) => ({ ...x, candidate_resume_id: "" })); return; }
    try {
      const r = await api.post(`/documents/seed-from-resume/${rid}`);
      setP((x) => ({ ...x, candidate_resume_id: rid, candidate_name: r.data.candidate_name, placeholders: { ...x.placeholders, ...r.data.placeholders } }));
      toast.success("Placeholders seeded from resume");
    } catch { toast.error("Failed to seed"); }
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/documents", { ...p, placeholders: p.placeholders });
        if (nextStatus && nextStatus !== "draft") await api.put(`/documents/${r.data.id}`, { status: nextStatus });
        toast.success("Document created");
      } else {
        const body = { title: p.title, body_html: p.body_html, placeholders: p.placeholders };
        if (nextStatus) body.status = nextStatus;
        await api.put(`/documents/${doc.id}`, body);
        toast.success("Saved");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const previewHtml = useMemo(() => {
    let out = p.body_html || "";
    for (const [k, v] of Object.entries(p.placeholders || {})) out = out.split(`{${k}}`).join(v || "");
    return out;
  }, [p.body_html, p.placeholders]);

  const loadLetterhead = async () => {
    if (isNew) {
      toast.info("Save the document first to see the letterhead preview.");
      return;
    }
    try {
      const r = await api.get(`/documents/${doc.id}/preview`);
      setLetterPreview(r.data.html);
      setMode("letterhead");
    } catch { toast.error("Preview failed"); }
  };

  // Tiny rich-text exec — writes directly to the textarea content. Uses simple
  // HTML tag insertion around selection for reliability in a <textarea>.
  const wrapSelection = (tag) => {
    const ta = document.querySelector('[data-testid="doc-body"]');
    if (!ta) return;
    const start = ta.selectionStart; const end = ta.selectionEnd;
    const val = ta.value;
    const sel = val.slice(start, end) || "text";
    const next = val.slice(0, start) + `<${tag}>${sel}</${tag}>` + val.slice(end);
    setP({ ...p, body_html: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + tag.length + 2, start + tag.length + 2 + sel.length); }, 0);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="doc-editor">
        <DialogHeader><DialogTitle>{isNew ? "New document" : `Edit · ${doc.title}`}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Input placeholder="Title" value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} data-testid="doc-title" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={p.type} onValueChange={(v) => setP({ ...p, type: v })}>
                <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={p.template_id || "__none__"} onValueChange={(v) => applyTemplate(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="doc-template"><SelectValue placeholder="Template" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">— No template —</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Input placeholder="Candidate / recipient name" value={p.candidate_name} onChange={(e) => setP({ ...p, candidate_name: e.target.value })} data-testid="doc-candidate" />
            {resumes.length > 0 && (
              <Select value={p.candidate_resume_id || "__none__"} onValueChange={seedFromResume}>
                <SelectTrigger data-testid="doc-from-resume"><SelectValue placeholder="(Optional) Seed from candidate resume…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No resume —</SelectItem>
                  {resumes.slice(0, 40).map((r) => (<SelectItem key={r.id} value={r.id}>{r.data?.name || r.title}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
            <div>
              <div className="text-xs font-semibold mb-1">Placeholders</div>
              <div className="text-[11px] text-muted-foreground mb-2">Use {PLACEHOLDER_HINTS.join(", ")} inside the body — set their values here:</div>
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
                {PLACEHOLDER_HINTS.map((ph) => {
                  const k = ph.replace(/[{}]/g, "");
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-xs font-mono w-40 text-muted-foreground">{ph}</span>
                      <Input value={p.placeholders?.[k] || ""} onChange={(e) => setP({ ...p, placeholders: { ...p.placeholders, [k]: e.target.value } })} className="h-8 text-sm" data-testid={`doc-ph-${k}`} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => wrapSelection("strong")} data-testid="doc-rte-bold"><strong>B</strong></Button>
              <Button size="sm" variant="outline" onClick={() => wrapSelection("em")} data-testid="doc-rte-italic"><em>i</em></Button>
              <Button size="sm" variant="outline" onClick={() => wrapSelection("u")} data-testid="doc-rte-underline"><span className="underline">U</span></Button>
              <Button size="sm" variant="outline" onClick={() => wrapSelection("h3")} data-testid="doc-rte-h3">H3</Button>
              <Button size="sm" variant="outline" onClick={() => setP({ ...p, body_html: (p.body_html || "") + "<p></p>" })} data-testid="doc-rte-p">+ Para</Button>
              <span className="text-[10px] text-muted-foreground ml-2">Tip: select text, click a button to wrap it.</span>
            </div>
            <Textarea rows={14} value={p.body_html} onChange={(e) => setP({ ...p, body_html: e.target.value })} placeholder="HTML body. Use <p>, <strong>, <em>, <br> and {placeholders}." className="font-mono text-xs" data-testid="doc-body" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-xs font-semibold flex items-center gap-1"><Eye className="size-3" /> {mode === "edit" ? "Live preview" : "With letterhead"}</div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant={mode === "edit" ? "default" : "outline"} onClick={() => setMode("edit")} data-testid="doc-preview-plain">Plain</Button>
                <Button size="sm" variant={mode === "letterhead" ? "default" : "outline"} onClick={loadLetterhead} data-testid="doc-preview-letterhead">With letterhead</Button>
              </div>
            </div>
            {mode === "edit" ? (
              <div className="border border-border rounded-md p-5 bg-white text-slate-900 text-sm min-h-[400px] max-h-[600px] overflow-y-auto" data-testid="doc-preview" dangerouslySetInnerHTML={{ __html: previewHtml || "<p class='text-slate-400'>Empty document</p>" }} />
            ) : (
              <div className="border border-border rounded-md p-0 bg-white text-slate-900 text-sm min-h-[400px] max-h-[600px] overflow-y-auto" data-testid="doc-preview-letterhead-body">
                <div className="p-5" dangerouslySetInnerHTML={{ __html: letterPreview || "<p class='text-slate-400'>Loading letterhead preview…</p>" }} />
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="doc-save-draft">Save draft</Button>
          <Button variant="outline" onClick={() => save("reviewed")} disabled={saving} data-testid="doc-save-reviewed">Mark reviewed</Button>
          <Button onClick={() => save("approved")} disabled={saving} className="gap-2" data-testid="doc-save-approved"><Check className="size-4" /> Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateList({ templates, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="flex justify-end mb-3"><Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="docs-new-template"><Plus className="size-4" /> New template</Button></div>
      {templates.length === 0 ? (
        <Card className="border-dashed border-border"><CardContent className="p-10 text-center text-sm text-muted-foreground">No templates yet. Create reusable letter blueprints here (e.g. "Offer Letter — Standard").</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tpl-list">
          {templates.map((t) => (
            <Card key={t.id} className="border-border" data-testid={`tpl-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => setEditing(t)} className="text-left flex-1 min-w-0">
                    <div className="font-bold truncate flex items-center gap-1.5">{t.locked && <Lock className="size-3 text-amber-500" />}{t.name}</div>
                    <div className="text-xs text-muted-foreground">{DOC_TYPES.find((x) => x.v === t.type)?.l}</div>
                  </button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={async () => { if (window.confirm("Delete template?")) { await api.delete(`/documents/templates/${t.id}`); toast.success("Deleted"); onRefresh(); } }} data-testid={`tpl-del-${t.id}`}><Trash2 className="size-4" /></Button>
                </div>
                {t.description && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {(editing || creating) && <TemplateEditor template={editing || {}} isNew={!editing} onClose={() => { setEditing(null); setCreating(false); onRefresh(); }} />}
    </>
  );
}

function TemplateEditor({ template, isNew, onClose }) {
  const [p, setP] = useState({
    name: template.name || "Untitled template",
    type: template.type || "custom",
    description: template.description || "",
    body_html: template.body_html || "<p>Dear {candidate_name},</p><p></p><p>Regards,</p><p>{hr_name}</p>",
    locked: template.locked || false,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/documents/templates", p);
      else await api.put(`/documents/templates/${template.id}`, p);
      toast.success("Saved");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto" data-testid="tpl-editor">
        <DialogHeader><DialogTitle>{isNew ? "New letter template" : `Edit · ${template.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Template name" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} data-testid="tpl-name" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={p.type} onValueChange={(v) => setP({ ...p, type: v })}>
              <SelectTrigger data-testid="tpl-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2 border border-border rounded-md px-3">
              {p.locked ? <Lock className="size-4 text-amber-500" /> : <Unlock className="size-4" />}
              <span className="text-sm">Lock template</span>
              <Switch checked={p.locked} onCheckedChange={(v) => setP({ ...p, locked: v })} data-testid="tpl-lock" />
            </div>
          </div>
          <Input placeholder="Description (optional)" value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} />
          <div className="text-xs text-muted-foreground">Use {PLACEHOLDER_HINTS.join(", ")} for dynamic values.</div>
          <Textarea rows={14} value={p.body_html} onChange={(e) => setP({ ...p, body_html: e.target.value })} placeholder="HTML body with placeholders" data-testid="tpl-body" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tpl-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalQueue({ docs, onRefresh }) {
  const approve = async (d) => {
    try { await api.put(`/documents/${d.id}`, { status: "approved" }); toast.success("Approved"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Only admins can approve"); }
  };
  const review = async (d) => {
    try { await api.put(`/documents/${d.id}`, { status: "reviewed" }); toast.success("Marked reviewed"); onRefresh(); }
    catch { toast.error("Failed"); }
  };
  const send = async (d) => {
    try { await api.put(`/documents/${d.id}`, { status: "sent" }); toast.success("Marked sent"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Only admins can send"); }
  };
  if (!docs.length) return <Card className="border-dashed border-border"><CardContent className="p-10 text-center text-sm text-muted-foreground">Approval queue is empty.</CardContent></Card>;
  return (
    <div className="space-y-2" data-testid="queue-list">
      {docs.map((d) => (
        <Card key={d.id} className="border-border">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <span className={`size-1.5 rounded-full ${STATUS_COLORS[d.status]}`} />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{d.title}</div>
              <div className="text-xs text-muted-foreground">{DOC_TYPES.find((x) => x.v === d.type)?.l} · {d.candidate_name || "—"}</div>
            </div>
            {d.status === "draft" && <Button size="sm" variant="outline" onClick={() => review(d)} data-testid={`queue-review-${d.id}`}>Mark reviewed</Button>}
            <Button size="sm" onClick={() => approve(d)} className="gap-1" data-testid={`queue-approve-${d.id}`}><Check className="size-3.5" /> Approve</Button>
            {d.status === "approved" && <Button size="sm" variant="outline" onClick={() => send(d)} className="gap-1" data-testid={`queue-send-${d.id}`}><Send className="size-3.5" /> Mark sent</Button>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
